import type {Action, State} from './types';
import {actionTypes} from './actions';

export const initialState: State<any> = {
  query: '',
  focus: false,
  selected: null,
  preselected: null,
};

/**
 * On a typical keyboard, the first number starts with a 1, goes up to 9,
 * and then ends at 0. This NUMBER_INDEXES_MAP ensures that if they press the number
 * `1`, that it indeed maps to the 0th index.
 */
const NUMBER_INDEXES_MAP = [
  9, // The key "0" is usually the 10th number, so it maps to the 9th index
  0, // The key "1" is the first item in the list (aka 0th index)
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
];

export const useFuzzySearchReducer = <Value>(
  state: State<Value>,
  action: Action<Value>
): State<Value> => {
  switch (action.type) {
    case actionTypes.FOCUSED: {
      return {...state, focus: true};
    }

    case actionTypes.BLURRED: {
      return {...state, focus: false};
    }

    case actionTypes.PATTERN_CHANGED: {
      return {
        ...state,
        preselected: null,
        query: action.payload,
      };
    }

    case actionTypes.MATCH_SELECTED: {
      return {
        ...state,
        focus: false,
        selected: action.payload,
      };
    }

    case actionTypes.MATCH_PRESELECTED: {
      return {
        ...state,
        selected: null,
        preselected: action.payload,
      };
    }

    case actionTypes.KEY_PRESSED: {
      const {key, results, metaKey, findNextIndex, tabToSelect, shiftKey} =
        action.payload;

      if (key === 'Escape') {
        return {
          ...state,
          query: '',
        };
      }

      if (results.length === 0) {
        return state;
      }

      if (metaKey && Number(key) >= 0 && Number(key) <= 9) {
        const number = NUMBER_INDEXES_MAP[Number(key)];

        // @ts-expect-error - TS18048 - 'number' is possibly 'undefined'.
        if (number >= results.length) {
          return state;
        }

        return {
          ...state,
          focus: false,
          // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
          selected: results[number].match,
        };
      }

      const {preselected} = state;
      const index = findNextIndex(results, preselected, key);

      const last = results.length - 1;

      if (key === 'ArrowUp') {
        return {
          ...state,
          preselected:
            preselected == null || index === -1
              ? // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                results[last].match
              : // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                results[index === 0 ? last : index - 1].match,
        };
      }

      if (key === 'ArrowDown') {
        return {
          ...state,
          preselected:
            preselected == null
              ? // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                results[0].match
              : // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                results[index === last ? 0 : index + 1].match,
        };
      }

      if (key === 'Enter' && preselected != null) {
        return {
          ...state,
          focus: false,
          selected: preselected,
        };
      }

      if (key === 'Tab' && tabToSelect && preselected != null && !shiftKey) {
        return {
          ...state,
          focus: false,
          selected: preselected,
        };
      }

      return state;
    }

    default:
      return state;
  }
};
